import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { CropPhoto } from '../../../../components/crop-photo';
import { AuthContext } from '../../../../contexts/auth-context';
import { uicolors } from '../../../../theme';

/**
 * Circular course progress indicator.
 * The last 10% renders as a three-row checkered finish line to signal "nearly done".
 */
export function CourseProgressRing(props: {
  value: number;
  avatarSrc?: string;
  avatarAlt?: string;
  size?: number;
  thickness?: number;
  onUploadSuccess?: () => void;
}) {
  // Props
  const { value, avatarSrc, avatarAlt, size = 200, thickness = 18 } = props;
  // Context
  const {
    state: { user },
  } = React.useContext(AuthContext);
  // State
  const [newPhoto, setNewPhoto] = React.useState('');

  const CHECKER_ZONE_START = 90;
  const RING_COUNT = 3;

  const cx = size / 2;
  const cy = size / 2;
  const clamped = Math.max(0, Math.min(100, value));
  const checkerZoneFraction = (100 - CHECKER_ZONE_START) / 100;

  // Full-thickness values for track and solid progress
  const fullRadius = (size - thickness) / 2;
  const fullCircumference = 2 * Math.PI * fullRadius;
  const solidLength =
    (Math.min(clamped, CHECKER_ZONE_START) / 100) * fullCircumference;
  const filledIntoChecker =
    (Math.max(0, clamped - CHECKER_ZONE_START) / 100) * fullCircumference;
  const fullZoneOffset = -fullCircumference * (CHECKER_ZONE_START / 100);
  const trackDash = `${
    (CHECKER_ZONE_START / 100) * fullCircumference
  } ${fullCircumference}`;

  // Build the N checker rings — each gets a radius, dash length, and offset.
  // Dash count is anchored to the outer ring so all rings share angular boundaries.
  const ringThickness = thickness / RING_COUNT;
  const outerRadius = (size - ringThickness) / 2;
  const outerCircumference = 2 * Math.PI * outerRadius;
  const outerCheckerLength = outerCircumference * checkerZoneFraction;
  const dashPairs = Math.max(
    3,
    Math.round(outerCheckerLength / (ringThickness * 2)),
  );

  const checkerRings = Array.from({ length: RING_COUNT }, (_, i) => {
    const radius = outerRadius - i * ringThickness;
    const circumference = 2 * Math.PI * radius;
    const dashLen = (circumference * checkerZoneFraction) / dashPairs / 2;
    const zoneOffset = -circumference * (CHECKER_ZONE_START / 100);
    const isOffsetRow = i % 2 === 1;
    return {
      radius,
      dashLen,
      offset: zoneOffset + (isOffsetRow ? dashLen : 0),
    };
  });

  const avatarSize = size - thickness * 2.6;

  // Upload Image
  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    accept: 'image/*',
    maxFiles: 1,
    onDrop: (acceptedFiles, fileRejections) => {
      // setErrorMessage('');
      // setUploadSuccess(false);
      if (fileRejections.length !== 0) {
        // setErrorMessage(fileRejections[0].errors[0].message);
      } else {
        const file = acceptedFiles[0];
        setNewPhoto(URL.createObjectURL(file));
      }
    },
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        position: 'relative',
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component="svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        sx={{ position: 'absolute', inset: 0 }}
      >
        {/* Background track for 0-90% */}
        <circle
          cx={cx}
          cy={cy}
          r={fullRadius}
          fill="none"
          stroke={uicolors.black['700']}
          strokeWidth={thickness}
          strokeDasharray={trackDash}
          transform={`rotate(-90 ${cx} ${cy})`}
        />

        {/* Three concentric dashed checker rings */}
        {checkerRings.map((r, i) => (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r.radius}
            fill="none"
            stroke="#ffffff"
            strokeWidth={ringThickness}
            strokeDasharray={`${r.dashLen} ${r.dashLen}`}
            strokeDashoffset={r.offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        ))}

        {/* Mask the checker rings outside the 90-100% zone */}
        <circle
          cx={cx}
          cy={cy}
          r={fullRadius}
          fill="none"
          stroke={uicolors.black['700']}
          strokeWidth={thickness + 2}
          strokeDasharray={trackDash}
          transform={`rotate(-90 ${cx} ${cy})`}
        />

        {/* Solid progress arc (0 to min(value, 90)) */}
        <circle
          cx={cx}
          cy={cy}
          r={fullRadius}
          fill="none"
          stroke={uicolors.green['500']}
          strokeWidth={thickness}
          strokeDasharray={`${solidLength} ${fullCircumference}`}
          strokeLinecap="butt"
          transform={`rotate(-90 ${cx} ${cy})`}
        />

        {/* Solid fill advancing through the checker zone past 90% */}
        {filledIntoChecker > 0 && (
          <circle
            cx={cx}
            cy={cy}
            r={fullRadius}
            fill="none"
            stroke={uicolors.green['500']}
            strokeWidth={thickness}
            strokeDasharray={`${filledIntoChecker} ${fullCircumference}`}
            strokeDashoffset={fullZoneOffset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}
      </Box>

      <CropPhoto
        agent={user.username}
        user={user}
        newPhoto={newPhoto}
        setUploadSuccess={() => {}}
        saved={() => {}}
        onSuccess={props.onUploadSuccess}
      />

      <input {...getInputProps()} />

      <Avatar
        component="button"
        type="button"
        src={avatarSrc}
        alt={avatarAlt}
        sx={{
          width: avatarSize,
          height: avatarSize,
          bgcolor: uicolors.black['700'],
          fontSize: avatarSize / 4,
          cursor: 'pointer',
          border: 'none',
          p: 0,
        }}
        onClick={() => open()}
      >
        <AddPhotoAlternateIcon sx={{ fontSize: avatarSize / 2 }} />
      </Avatar>
    </Box>
  );
}
